import { jsonParseLinter } from "@codemirror/lang-json";
import { syntaxTree } from "@codemirror/language";
import type { Diagnostic } from "@codemirror/lint";
import type { EditorState } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import type { SyntaxNode } from "@lezer/common";
import { schemasApi, servicesApi } from "@/api/client";
import type { Service } from "@/types/api";
import {
  hasUserDefinedResources,
  isLlmProvider,
  PROVIDER_LABELS,
  SCHEMA_REQUIRED_SOURCE_PROVIDERS,
  sourceEncodingIsChosen,
  sourceEncodingRule,
} from "@/utils/providerCapabilities";
import { SCHEMA_CAPABLE_PROVIDERS } from "@/utils/schemaServices";
import { closestMatches } from "./closestMatches";
import { checkRouting } from "./pipelineRoutingLint";
import {
  type SourceFilter,
  sourceFilterError,
  sourceFilterOf,
} from "./pipelineSourceFilter";
import { canStageAs } from "./pipelineStaging";
import { PLACEHOLDER_STAGES } from "./pipelineTemplate";

// Lint for the Full pipeline code editor: JSON syntax (Monaco-worker parity
// via jsonParseLinter), the pipeline shape /studio's CreateJobRequest
// expects, and live reference existence — /studio ran the same
// service/resource/schema lookups (validatePipelineRefs) only at deploy
// time; here they run as you type, TTL-cached. Structural/reference errors
// carry fix data (missing keys, valid encodings, "did you mean"
// replacements) for whoever repairs the code; the editor itself offers no
// buttons — the status bar's Errors card lists the problems and hands them
// to the AI Helper.

const ENCODINGS = new Set(["bson", "json", "avro"]);
const ENCODING_LIST = ["bson", "json", "avro"];

export { closestMatches };

export type Path = Array<string | number>;

// Lightweight domain aliases for the primitive arguments below — they name
// what a value means at a call site (a JSON key vs. an engine service name
// vs. a document offset) without runtime cost.
type JsonKey = string;
type ErrorLabel = string;
type ServiceName = string;
type ProviderName = string;
type DocPosition = number;
type ListIndex = number;

/** Machine-applicable fix attached to an error: insert a missing key into
 *  the object at `path`, or replace the value at `path` with one of the
 *  suggested strings. Data only — never rendered as tooltip buttons. */
export type PipelineFix =
  | { kind: "addKey"; key: JsonKey; value: JsonValue }
  | { kind: "replaceValue"; replacements: string[] };

/** What an addKey fix inserts: a string default, or a whole placeholder
 *  entry / list for a missing stage section. */
type JsonValue = string | Record<string, unknown> | unknown[];

export interface PipelineDocError {
  path: Path;
  message: string;
  fix?: PipelineFix;
  /** Absent = error. Warnings are the placeholder entries' empty fields:
   *  a yellow gutter mark and tooltip (no underline — the ghost hint sits
   *  there), and the "placeholders" lint status that keeps Run, Deploy and
   *  the Stages switch waiting. Not the user's mistake. */
  severity?: "warning";
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function missingString(obj: Record<string, unknown>, key: JsonKey): boolean {
  return typeof obj[key] !== "string" || obj[key] === "";
}

// An entry whose reference fields are all present and all "" is a
// placeholder — the shape the template ships. It is not a mistake: its
// empty fields are warnings ("Choose a …"), not errors. Once any of the
// fields is filled the empty ones are errors: the fields depend on each
// other.
function isPlaceholderEntry(
  obj: Record<string, unknown>,
  keys: readonly JsonKey[],
): boolean {
  return keys.every((key) => obj[key] === "");
}

// The reference fields of a stage / schema entry. A placeholder entry gets
// a warning per hinted field, on the value (where the editor draws the
// placeholder hint). Otherwise absent, non-string or empty fields are
// errors — an empty one on the value, an absent one on the entry with an
// insert fix.
function checkReferenceFields(
  obj: Record<string, unknown>,
  keys: readonly JsonKey[],
  hints: Record<string, string>,
  label: ErrorLabel,
  path: Path,
  errors: PipelineDocError[],
): void {
  if (isPlaceholderEntry(obj, keys)) {
    for (const key of keys) {
      const hint = hints[key];
      if (hint) {
        errors.push({
          path: [...path, key],
          message: hint,
          severity: "warning",
        });
      }
    }
    return;
  }
  for (const key of keys) {
    if (obj[key] === "") {
      errors.push({
        path: [...path, key],
        message: `${label} is missing "${key}"`,
      });
      continue;
    }
    if (missingString(obj, key)) {
      errors.push({
        path,
        message: `${label} is missing "${key}"`,
        // Only insertable when the key is absent entirely.
        ...(key in obj ? {} : { fix: { kind: "addKey", key, value: "" } }),
      });
    }
  }
}

const STAGE_REFERENCE_KEYS = ["service_name", "resource"] as const;
const STAGE_HINTS = {
  service_name: "Choose a service",
  resource: "Choose a resource",
};
const SCHEMA_REFERENCE_KEYS = ["id", "service_name", "resource"] as const;
// No hint for id: the schemas block's ids are the user's own aliases.
const SCHEMA_HINTS = {
  service_name: "Choose a service",
  resource: "Choose a schema",
};

function checkEncoding(
  stage: Record<string, unknown>,
  key: "output_encoding" | "input_encoding",
  path: Path,
  errors: PipelineDocError[],
): void {
  if (key in stage && !ENCODINGS.has(stage[key] as string)) {
    errors.push({
      path: [...path, key],
      message: `${key} must be "bson", "json" or "avro"`,
      fix: { kind: "replaceValue", replacements: ENCODING_LIST },
    });
  }
}

function checkStage(
  stage: unknown,
  label: ErrorLabel,
  path: Path,
  errors: PipelineDocError[],
): void {
  if (!isObject(stage)) {
    errors.push({ path, message: `${label} must be an object` });
    return;
  }
  checkReferenceFields(
    stage,
    STAGE_REFERENCE_KEYS,
    STAGE_HINTS,
    label,
    path,
    errors,
  );
  if (!("output_encoding" in stage)) {
    errors.push({
      path,
      message: `${label} is missing "output_encoding"`,
      fix: { kind: "addKey", key: "output_encoding", value: "json" },
    });
  }
  checkEncoding(stage, "output_encoding", path, errors);
  checkEncoding(stage, "input_encoding", path, errors);
}

function checkSchema(
  schema: unknown,
  index: ListIndex,
  errors: PipelineDocError[],
): void {
  const path: Path = ["schemas", index];
  const label = `schemas[${index}]`;
  if (!isObject(schema)) {
    errors.push({ path, message: `${label} must be an object` });
    return;
  }
  checkReferenceFields(
    schema,
    SCHEMA_REFERENCE_KEYS,
    SCHEMA_HINTS,
    label,
    path,
    errors,
  );
}

function checkName(
  doc: Record<string, unknown>,
  errors: PipelineDocError[],
): void {
  if (!missingString(doc, "name")) return;
  errors.push({
    path: [],
    message: 'Pipeline is missing "name"',
    ...("name" in doc
      ? {}
      : { fix: { kind: "addKey", key: "name", value: "my-pipeline" } }),
  });
}

// A pipeline without a source has nothing to run and the engine refuses it
// at deploy — the same error as a missing name, with the template's
// placeholder entry as the insert.
function checkSource(
  doc: Record<string, unknown>,
  errors: PipelineDocError[],
): void {
  if ("source" in doc) {
    checkStage(doc.source, "source", ["source"], errors);
    return;
  }
  errors.push({
    path: [],
    message: 'Pipeline is missing "source"',
    fix: { kind: "addKey", key: "source", value: PLACEHOLDER_STAGES.source },
  });
}

// Likewise a pipeline without a sink — unless the code declares
// `mode = "inventory"`, which the engine runs without one.
function checkSinks(
  doc: Record<string, unknown>,
  errors: PipelineDocError[],
): void {
  checkStageSection(doc, "sinks", errors);
  if (doc.mode === "inventory") return;
  if (!("sinks" in doc)) {
    errors.push({
      path: [],
      message: 'Pipeline is missing "sinks"',
      fix: { kind: "addKey", key: "sinks", value: [PLACEHOLDER_STAGES.sink] },
    });
  } else if (isEmptyList(doc.sinks)) {
    errors.push({ path: ["sinks"], message: '"sinks" must not be empty' });
  }
}

function isEmptyList(value: unknown): boolean {
  return Array.isArray(value) && value.length === 0;
}

function checkStageSection(
  doc: Record<string, unknown>,
  section: "middlewares" | "sinks",
  errors: PipelineDocError[],
): void {
  if (!(section in doc)) return;
  const list = doc[section];
  if (!Array.isArray(list)) {
    errors.push({ path: [section], message: `"${section}" must be an array` });
    return;
  }
  list.forEach((stage, i) => {
    checkStage(stage, `${section}[${i}]`, [section, i], errors);
  });
}

function checkSchemasSection(
  doc: Record<string, unknown>,
  errors: PipelineDocError[],
): void {
  if (!("schemas" in doc)) return;
  if (!Array.isArray(doc.schemas)) {
    errors.push({ path: ["schemas"], message: '"schemas" must be an array' });
    return;
  }
  doc.schemas.forEach((schema, i) => {
    checkSchema(schema, i, errors);
  });
}

// The ids the schemas block defines — the only names stage schema_id refs
// can resolve against.
function schemaBlockIds(doc: Record<string, unknown>): Set<string> {
  const ids = new Set<string>();
  if (!Array.isArray(doc.schemas)) return ids;
  for (const entry of doc.schemas) {
    if (isObject(entry) && typeof entry.id === "string") ids.add(entry.id);
  }
  return ids;
}

// The error for one stage's schema_id, or null when it resolves (or the
// stage carries none). A stage schema_id the schemas block doesn't define
// deploys fine but provisions with NO schema applied and zero signal (the
// engine falls back to Schema::Undefined — TTTL-138), so the dangling ref
// is an error here.
function schemaRefError(
  stage: unknown,
  label: ErrorLabel,
  path: Path,
  blockIds: Set<string>,
): PipelineDocError | null {
  if (!isObject(stage)) return null;
  if (!("schema_id" in stage)) return null;
  const ref = stage.schema_id;
  const refPath: Path = [...path, "schema_id"];
  if (typeof ref !== "string") {
    return { path: refPath, message: `${label} schema_id must be a string` };
  }
  if (blockIds.has(ref)) return null;
  // Unlike service/resource names, the valid ids are the handful the block
  // defines — when none is edit-distance-close, offer them all instead of
  // no fix.
  const close = closestMatches(ref, blockIds);
  const replacements = close.length > 0 ? close : [...blockIds].slice(0, 3);
  return {
    path: refPath,
    message: `${label} schema_id "${ref}" has no matching entry in "schemas"`,
    ...(replacements.length > 0
      ? { fix: { kind: "replaceValue", replacements } }
      : {}),
  };
}

// Every stage position a schema_id can sit on, with its label and path.
function stagePositions(
  doc: Record<string, unknown>,
): Array<{ stage: unknown; label: ErrorLabel; path: Path }> {
  const positions = [
    { stage: doc.source, label: "source", path: ["source"] as Path },
  ];
  for (const section of ["middlewares", "sinks"] as const) {
    const list = doc[section];
    if (!Array.isArray(list)) continue;
    list.forEach((stage, i) => {
      positions.push({ stage, label: `${section}[${i}]`, path: [section, i] });
    });
  }
  return positions;
}

function checkSchemaRefs(
  doc: Record<string, unknown>,
  errors: PipelineDocError[],
): void {
  const blockIds = schemaBlockIds(doc);
  for (const { stage, label, path } of stagePositions(doc)) {
    const error = schemaRefError(stage, label, path, blockIds);
    if (error) errors.push(error);
  }
}

// A pipeline names a resource once, whatever the role: Stages keys its
// rows by service + resource and a staged row leaves the explorer's pool,
// so a second mention has no row to hand over to. The engine would run it
// (a script twice, a double write, a source feeding itself) — Full follows
// Stages here. The error lands on the later entry. Schemas are exempt: one
// schema serves any number of stages. Placeholder entries name nothing.
function checkRepeatedResources(
  doc: Record<string, unknown>,
  errors: PipelineDocError[],
): void {
  const firstAt = new Map<string, ErrorLabel>();
  for (const { stage, label, path } of stagePositions(doc)) {
    if (!isObject(stage)) continue;
    const { service_name: service, resource } = stage;
    if (typeof service !== "string" || typeof resource !== "string") continue;
    if (service === "" || resource === "") continue;
    const key = JSON.stringify([service, resource]);
    const first = firstAt.get(key);
    if (first === undefined) {
      firstAt.set(key, label);
      continue;
    }
    errors.push({
      path,
      message: `${label} repeats ${first}: a pipeline can use a resource only once`,
    });
  }
}

/** Structural validation of a parsed pipeline document. */
export function validatePipelineDoc(doc: unknown): PipelineDocError[] {
  if (!isObject(doc)) {
    return [{ path: [], message: "Pipeline must be a JSON object" }];
  }
  const errors: PipelineDocError[] = [];
  checkName(doc, errors);
  checkSource(doc, errors);
  checkStageSection(doc, "middlewares", errors);
  checkSinks(doc, errors);
  checkSchemasSection(doc, errors);
  checkSchemaRefs(doc, errors);
  checkRouting(doc, stagePositions(doc), errors);
  checkRepeatedResources(doc, errors);
  return errors;
}

// Array punctuation tokens in the lang-json tree; every other child is a
// value.
const JSON_PUNCTUATION = new Set(["[", "]", ",", "{", "}"]);

// The value nodes of a lang-json Array node, in order.
function arrayValues(node: SyntaxNode): SyntaxNode[] {
  const values: SyntaxNode[] = [];
  for (let child = node.firstChild; child; child = child.nextSibling) {
    if (!JSON_PUNCTUATION.has(child.name)) values.push(child);
  }
  return values;
}

// The indexth element of a lang-json Array node, or null.
function arrayElementAt(node: SyntaxNode, index: ListIndex): SyntaxNode | null {
  if (node.name !== "Array") return null;
  return arrayValues(node)[index] ?? null;
}

// The value node of an Object property named `key`, or null — an incomplete
// property (no value after the ":") resolves to null too.
function propertyValueOf(
  node: SyntaxNode,
  doc: EditorState["doc"],
  key: JsonKey,
): SyntaxNode | null {
  if (node.name !== "Object") return null;
  for (
    let property = node.firstChild;
    property;
    property = property.nextSibling
  ) {
    if (property.name !== "Property") continue;
    const keyNode = property.firstChild;
    if (!keyNode) continue;
    if (doc.sliceString(keyNode.from, keyNode.to) !== `"${key}"`) continue;
    const last = property.lastChild;
    if (!last) return null;
    if (last.name === "PropertyName") return null;
    if (last.name === ":") return null;
    return last;
  }
  return null;
}

// Walks a document path through the lang-json syntax tree. `resolved` is
// false when the walk stopped early; `range` then covers the nearest
// resolvable ancestor (or the document start).
function walkJsonPath(
  state: EditorState,
  path: Path,
): {
  node: SyntaxNode | null;
  range: { from: number; to: number };
  resolved: boolean;
} {
  let node = syntaxTree(state).topNode.firstChild;
  let range = { from: 0, to: Math.min(state.doc.length, 1) };

  for (const segment of path) {
    if (!node) return { node, range, resolved: false };
    const next =
      typeof segment === "number"
        ? arrayElementAt(node, segment)
        : propertyValueOf(node, state.doc, segment);
    if (!next) return { node, range, resolved: false };
    node = next;
    range = { from: node.from, to: node.to };
  }

  return { node, range, resolved: true };
}

// Resolves a document path to the corresponding node range in the lang-json
// syntax tree, so diagnostics underline the offending value. Falls back to
// the nearest resolvable ancestor (or the whole document).
export function findNodeRange(
  state: EditorState,
  path: Path,
): { from: number; to: number } {
  return walkJsonPath(state, path).range;
}

/** The exact syntax node at a document path, or null when the path doesn't
 *  fully resolve — the insertion target for addKey fixes. */
export function jsonNodeAt(state: EditorState, path: Path): SyntaxNode | null {
  const { node, resolved } = walkJsonPath(state, path);
  return resolved ? node : null;
}

// Graph node id prefixes of the two stage list sections.
const STAGE_LIST_PREFIX: Record<string, string> = {
  '"middlewares"': "mw",
  '"sinks"': "sink",
};

// The `<prefix>-<index>` id of the list element containing `pos`, or null.
function stageIndexAt(
  list: SyntaxNode,
  pos: DocPosition,
  prefix: ErrorLabel,
): string | null {
  const index = arrayValues(list).findIndex(
    (el) => pos >= el.from && pos <= el.to,
  );
  if (index === -1) return null;
  return `${prefix}-${index}`;
}

// The stage id for a position known to be inside this top-level property.
function stageIdInProperty(
  state: EditorState,
  prop: SyntaxNode,
  pos: DocPosition,
): string | null {
  const keyNode = prop.firstChild;
  if (!keyNode) return null;
  const key = state.doc.sliceString(keyNode.from, keyNode.to);
  if (key === '"source"') return "source";
  const prefix = STAGE_LIST_PREFIX[key];
  if (!prefix) return null;
  const list = prop.lastChild;
  if (!list) return null;
  if (list.name !== "Array") return null;
  return stageIndexAt(list, pos, prefix);
}

/** The graph node id (source / mw-<i> / sink-<i>) of the stage enclosing a
 *  document position, or null when the position isn't inside a stage — how
 *  the code editor tells the graph which card the cursor is on. */
export function stageIdAtPosition(
  state: EditorState,
  pos: DocPosition,
): string | null {
  const root = syntaxTree(state).topNode.firstChild;
  if (!root) return null;
  if (root.name !== "Object") return null;
  for (let prop = root.firstChild; prop; prop = prop.nextSibling) {
    if (prop.name !== "Property") continue;
    if (pos < prop.from) continue;
    if (pos > prop.to) continue;
    return stageIdInProperty(state, prop, pos);
  }
  return null;
}

export interface PipelineRef {
  kind: "stage" | "schema";
  role: "source" | "middleware" | "sink" | "schema";
  /** Path to the ref's object node (service_name/resource live under it). */
  path: Path;
  serviceName: ServiceName;
  resource: string;
  /** The stage's `schema_id`, when it declares one. */
  schemaId?: string;
  /** The source only: the filter the pipeline puts on it, when it is well
   *  formed. Whether the source's provider can take one is a reference
   *  check, since it needs the service's provider. */
  sourceFilter?: SourceFilter;
  /** The stage's `input_encoding` / `output_encoding` as written, when the
   *  key is present (any type: the structural lint owns the value, these
   *  rules only ask whether it is there and what it says). */
  inputEncoding?: unknown;
  outputEncoding?: unknown;
}

function refOf(
  kind: PipelineRef["kind"],
  role: PipelineRef["role"],
  path: Path,
  value: unknown,
): PipelineRef | null {
  if (!isObject(value)) return null;
  const serviceName = value.service_name;
  // Structural lint already covers a missing/empty service name; a
  // missing/empty resource still yields the ref (as "") so the service's
  // role is checked as soon as it is named — refError skips the resource
  // checks for it.
  if (typeof serviceName !== "string" || serviceName === "") return null;
  const resource = typeof value.resource === "string" ? value.resource : "";
  const schemaId = value.schema_id;
  return {
    kind,
    role,
    path,
    serviceName,
    resource,
    ...(typeof schemaId === "string" && schemaId !== "" ? { schemaId } : {}),
    ...("input_encoding" in value
      ? { inputEncoding: value.input_encoding }
      : {}),
    ...("output_encoding" in value
      ? { outputEncoding: value.output_encoding }
      : {}),
  };
}

/** Service/resource/schema references in a parsed pipeline document —
 *  /studio's collectStageRefs plus schema refs, with JSON paths. */
export function collectPipelineRefs(doc: unknown): PipelineRef[] {
  if (!isObject(doc)) return [];
  const refs: Array<PipelineRef | null> = [];

  if ("source" in doc) {
    const source = refOf("stage", "source", ["source"], doc.source);
    const sourceFilter = sourceFilterOf(doc);
    refs.push(source && sourceFilter ? { ...source, sourceFilter } : source);
  }
  for (const [section, role] of [
    ["middlewares", "middleware"],
    ["sinks", "sink"],
  ] as const) {
    const list = doc[section];
    if (!Array.isArray(list)) continue;
    list.forEach((stage, i) => {
      refs.push(refOf("stage", role, [section, i], stage));
    });
  }
  if (Array.isArray(doc.schemas)) {
    doc.schemas.forEach((schema, i) => {
      refs.push(refOf("schema", "schema", ["schemas", i], schema));
    });
  }
  return refs.filter((r): r is PipelineRef => r !== null);
}

/** Resolved lookups for refErrors. A missing map entry means the lookup was
 *  unavailable (fetch failed / not attempted) — those checks are skipped so
 *  transient API errors never produce false positives. */
export interface RefLookups {
  /** service name → provider; services absent from the engine are simply
   *  not in the map, so presence is tracked via `providers.has`. */
  providers: Map<string, string>;
  /** service name → known resource names. */
  resources: Map<string, Set<string>>;
  /** schema service name → known schema ids and names. */
  schemas: Map<string, Set<string>>;
}

// /studio's exemptions (usePipelineState): resources that cannot be listed are
// user-defined, so there is nothing to check them against; mongodb and http
// sinks are auto-created on first write.
function skipResourceCheck(
  provider: ProviderName,
  role: PipelineRef["role"],
): boolean {
  if (hasUserDefinedResources(provider)) return true;
  return (provider === "mongodb" || provider === "http") && role === "sink";
}

/** Whether a service of this provider can be referenced in the given role —
 *  the staging rules (canStageAs) plus the schema-service rule. */
export function providerFitsRole(
  role: PipelineRef["role"],
  provider: ProviderName,
): boolean {
  const p = provider as Service["provider"];
  if (role === "source") return canStageAs("source", p);
  if (role === "middleware") return canStageAs("transform", p);
  if (role === "sink") return canStageAs("sink", p);
  return SCHEMA_CAPABLE_PROVIDERS.has(provider);
}

function replacementFix(
  target: JsonKey,
  candidates: Iterable<string>,
): { fix: PipelineFix } | Record<string, never> {
  const replacements = closestMatches(target, candidates);
  return replacements.length > 0
    ? { fix: { kind: "replaceValue", replacements } }
    : {};
}

type RefLookupMaps = {
  providers: Map<string, string | null> | Map<string, string>;
  resources: Map<string, Set<string>>;
  schemas: Map<string, Set<string>>;
};

// Unknown-service error with role-aware "did you mean" candidates.
function unknownServiceError(
  ref: PipelineRef,
  providers: RefLookupMaps["providers"],
): PipelineDocError {
  const candidates = [...providers.entries()]
    .filter(([, p]) => p !== null && providerFitsRole(ref.role, p))
    .map(([name]) => name);
  return {
    path: [...ref.path, "service_name"],
    message: `Service "${ref.serviceName}" not found`,
    ...replacementFix(ref.serviceName, candidates),
  };
}

// Why a known service can't be used in this role — the same rules the
// picker applies (providerFitsRole), worded for the tooltip.
function wrongRoleReason(
  role: PipelineRef["role"],
  provider: ProviderName,
): string {
  if (isLlmProvider(provider)) return "AI services can't be used in pipelines";
  if (role === "schema") {
    return "schemas live in MongoDB, Kafka or Google Pub/Sub services";
  }
  if (role === "middleware") {
    return "only Scripts (UDF) services can be middlewares";
  }
  if (provider === "udf") return "scripts can only be middlewares";
  // A source-only provider (HTTP source, generator, scanner) written as a
  // sink — the only remaining way a source/sink role can be refused.
  return "it can only be a source";
}

const MAX_ROLE_REPLACEMENTS = 5;

// A service that exists but can't play the role it was written in: the
// picker never offers it, so a hand-typed one gets the same rule as an
// error on the value, with the services that DO fit as the replacements
// (names in the wrong role never resemble the right ones, so this lists
// fitting services rather than close matches). Null when the role fits.
function wrongRoleError(
  ref: PipelineRef,
  provider: ProviderName,
  providers: RefLookupMaps["providers"],
): PipelineDocError | null {
  if (providerFitsRole(ref.role, provider)) return null;
  const label = PROVIDER_LABELS[provider as Service["provider"]] ?? provider;
  const article = /^(?:[AEIOU]|HTTP)/.test(label) ? "an" : "a";
  const fitting = [...providers.entries()]
    .filter(([, p]) => p !== null && providerFitsRole(ref.role, p))
    .map(([name]) => name)
    .slice(0, MAX_ROLE_REPLACEMENTS);
  return {
    path: [...ref.path, "service_name"],
    message: `"${ref.serviceName}" is ${article} ${label} service — ${wrongRoleReason(ref.role, provider)}`,
    ...(fitting.length > 0
      ? { fix: { kind: "replaceValue", replacements: fitting } }
      : {}),
  };
}

// Missing-name error against a known-name set, or null when the name exists
// or the lookup wasn't available (no false positives on API failures).
function unknownNameError(
  ref: PipelineRef,
  known: Set<string> | undefined,
  label: "Schema" | "Resource",
): PipelineDocError | null {
  if (!known) return null;
  if (known.has(ref.resource)) return null;
  return {
    path: [...ref.path, "resource"],
    message: `${label} "${ref.resource}" not found in service "${ref.serviceName}"`,
    ...replacementFix(ref.resource, known),
  };
}

// The single ref's error, or null when it resolves (or its check is skipped).
function refError(
  ref: PipelineRef,
  provider: ProviderName,
  lookups: RefLookupMaps,
): PipelineDocError | null {
  if (ref.kind === "schema") {
    if (ref.resource === "") return null;
    return unknownNameError(
      ref,
      lookups.schemas.get(ref.serviceName),
      "Schema",
    );
  }
  const missingSchema = missingRequiredSchema(ref, provider);
  if (missingSchema) return missingSchema;
  // An empty resource is the structural lint's business (placeholder or
  // "is missing" error) — nothing to look up.
  if (ref.resource === "") return null;
  if (skipResourceCheck(provider, ref.role)) return null;
  return unknownNameError(
    ref,
    lookups.resources.get(ref.serviceName),
    "Resource",
  );
}

/** A source that builds its documents from a schema cannot run without one.
 *  The engine refuses such a pipeline at start; catching it here means the
 *  user learns why while still editing. */
function missingRequiredSchema(
  ref: PipelineRef,
  provider: ProviderName,
): PipelineDocError | null {
  const required =
    ref.role === "source" &&
    SCHEMA_REQUIRED_SOURCE_PROVIDERS.has(provider as Service["provider"]);
  if (!required || ref.schemaId) return null;

  return {
    path: ref.path,
    message: `A ${provider} source needs a schema — it defines the documents to produce. Add schema_id.`,
    fix: { kind: "addKey", key: "schema_id", value: "" },
  };
}

/** Kafka and Pub/Sub sources have no engine default for their input
 *  encoding: the pipeline fails to start with "input encoding not found".
 *  Present-but-invalid values belong to the structural lint (checkEncoding),
 *  so only absence is reported here. */
function missingInputEncoding(
  ref: PipelineRef,
  provider: ProviderName,
): PipelineDocError | null {
  if (ref.role !== "source" || ref.inputEncoding !== undefined) return null;
  if (sourceEncodingRule(provider).kind !== "required") return null;
  const label = PROVIDER_LABELS[provider as Service["provider"]] ?? provider;
  return {
    path: ref.path,
    message: `A ${label} source needs "input_encoding" — the engine has no default for it.`,
    fix: { kind: "addKey", key: "input_encoding", value: "json" },
  };
}

/** A MongoDB sink persists bson only: on json the engine rejects every
 *  write ("JSON persistence is disabled") while the job stays running, so
 *  nothing in the UI would say why no data arrives. */
function mongoSinkEncoding(
  ref: PipelineRef,
  provider: ProviderName,
): PipelineDocError | null {
  if (ref.role !== "sink" || provider !== "mongodb") return null;
  const value = ref.outputEncoding;
  if (typeof value !== "string" || value === "bson") return null;
  return {
    path: [...ref.path, "output_encoding"],
    message: `A MongoDB sink must write bson — the engine refuses ${value} and the job keeps running without writing anything.`,
    fix: { kind: "replaceValue", replacements: ["bson"] },
  };
}

/** Avro frames carry no schema of their own: a source that delivers Avro
 *  without a schema_id cannot decode a single record. Only where the
 *  encoding is the user's choice — fixed-encoding providers ignore it. */
function avroSourceNeedsSchema(
  ref: PipelineRef,
  provider: ProviderName,
): PipelineDocError | null {
  if (ref.role !== "source" || ref.schemaId) return null;
  if (ref.inputEncoding !== "avro") return null;
  if (!sourceEncodingIsChosen(provider)) return null;
  return {
    path: ref.path,
    message:
      "An Avro source needs a schema — the engine cannot decode a record without one. Add schema_id.",
    fix: { kind: "addKey", key: "schema_id", value: "" },
  };
}

export function refErrors(
  refs: PipelineRef[],
  lookups: RefLookupMaps,
): PipelineDocError[] {
  const errors: Array<PipelineDocError | null> = [];
  for (const ref of refs) {
    const provider = lookups.providers.get(ref.serviceName);
    errors.push(
      provider == null
        ? unknownServiceError(ref, lookups.providers)
        : (wrongRoleError(ref, provider, lookups.providers) ??
            refError(ref, provider, lookups)),
    );
    if (provider != null && ref.sourceFilter) {
      errors.push(sourceFilterError(ref.sourceFilter, provider));
    }
    if (provider != null) {
      errors.push(
        missingInputEncoding(ref, provider),
        avroSourceNeedsSchema(ref, provider),
        mongoSinkEncoding(ref, provider),
      );
    }
  }
  return errors.filter((e): e is PipelineDocError => e !== null);
}

// Lookup cache: lint runs on every pause in typing, so results are reused
// for a short window instead of re-hitting the API per keystroke. Failed
// fetches resolve to null and their checks are skipped (no false positives).
const CACHE_TTL_MS = 15_000;

interface CacheEntry<T> {
  at: number;
  value: Promise<T | null>;
}

function cached<T>(
  store: Map<string, CacheEntry<T>>,
  key: JsonKey,
  fetcher: () => Promise<T>,
): Promise<T | null> {
  const hit = store.get(key);
  const now = Date.now();
  if (hit && now - hit.at < CACHE_TTL_MS) return hit.value;
  const value = fetcher().catch(() => null);
  store.set(key, { at: now, value });
  return value;
}

const servicesCache = new Map<string, CacheEntry<Map<string, string>>>();
const resourcesCache = new Map<string, CacheEntry<Set<string>>>();
const schemasCache = new Map<string, CacheEntry<Set<string>>>();

/** Cached service name → provider map (null while the API is unreachable).
 *  Shared by the linter and the completion source. */
export function lookupProviders(): Promise<Map<string, string> | null> {
  return cached(servicesCache, "all", async () => {
    const services = await servicesApi.list();
    return new Map(services.map((s) => [s.name, s.provider as string]));
  });
}

/** Cached resource names of a service. */
export function lookupResources(
  serviceName: ServiceName,
): Promise<Set<string> | null> {
  return cached(resourcesCache, serviceName, async () => {
    const { resources } = await servicesApi.listResources(serviceName);
    return new Set(resources.map((r) => r.name));
  });
}

/** Cached schema ids and names of a schema service. */
export function lookupSchemas(
  serviceName: ServiceName,
): Promise<Set<string> | null> {
  return cached(schemasCache, serviceName, async () => {
    const list = await schemasApi.list(serviceName);
    const ids = new Set<string>();
    for (const s of list) {
      ids.add(s.id);
      if (s.name) ids.add(s.name);
    }
    return ids;
  });
}

/** The lookups refErrors needs for a set of refs, from the TTL caches —
 *  shared by the linter and the graph strip's unresolved-card flags. */
export async function fetchRefLookups(refs: PipelineRef[]): Promise<{
  providers: Map<string, string>;
  resources: Map<string, Set<string>>;
  schemas: Map<string, Set<string>>;
}> {
  const resources = new Map<string, Set<string>>();
  const schemas = new Map<string, Set<string>>();
  if (refs.length === 0) return { providers: new Map(), resources, schemas };

  const providers = await lookupProviders();
  // Service list unavailable — mark every referenced service as present so
  // nothing gets flagged, and skip the per-service lookups.
  if (!providers) {
    return {
      providers: new Map(refs.map((r) => [r.serviceName, "unknown"])),
      resources,
      schemas,
    };
  }

  await Promise.all(
    refs.map(async (ref) => {
      const provider = providers.get(ref.serviceName);
      if (provider === undefined) return;
      if (ref.kind === "schema") {
        if (schemas.has(ref.serviceName)) return;
        const known = await lookupSchemas(ref.serviceName);
        if (known) schemas.set(ref.serviceName, known);
        return;
      }
      if (skipResourceCheck(provider, ref.role)) return;
      if (resources.has(ref.serviceName)) return;
      const known = await lookupResources(ref.serviceName);
      if (known) resources.set(ref.serviceName, known);
    }),
  );

  return { providers, resources, schemas };
}

/** Settle state of the async lint pass — the validity tick shows a pending
 *  state instead of a stale green while reference checks are in flight, and
 *  save/run (once it exists) should gate on "clean".
 *  "empty": the document has no text — nothing to check and nothing to
 *  run; the editor shows its ghost template instead. */
export type LintStatus =
  | "pending"
  | "clean"
  | "errors"
  | "empty"
  | "placeholders";

/** Wraps an async lint source so the UI can track whether checks are in
 *  flight: "pending" fires when a run starts; when the latest run settles,
 *  "empty" for a blank document, "errors" when any diagnostic is an error,
 *  "placeholders" when the only diagnostics are the placeholder warnings
 *  (nothing wrong, nothing to run either), else "clean". A run superseded
 *  by a newer one never reports its settled status, so out-of-order
 *  resolutions can't flash a stale verdict. */
export function statusTrackingLinter(
  source: (view: EditorView) => Promise<Diagnostic[]>,
  onStatus: (status: LintStatus) => void,
  /** The settled pass's diagnostics, for verdicts beyond the status. */
  onSettled?: (view: EditorView, diagnostics: Diagnostic[]) => void,
  /** Doc text of the last settled pass, shared with remounting editors.
   *  Re-linting that exact text keeps the shown verdict instead of
   *  downgrading to pending — the pass still runs and re-reports, so the
   *  header's Unresolved chip doesn't blink on a view round trip. */
  settledFor?: { current: string | null },
): (view: EditorView) => Promise<Diagnostic[]> {
  let generation = 0;
  return async (view) => {
    const run = ++generation;
    // Captured at run start: the text these diagnostics are computed for.
    const text = settledFor ? view.state.doc.toString() : null;
    if (!settledFor || settledFor.current !== text) onStatus("pending");
    const diagnostics = await source(view);
    if (run === generation) {
      onStatus(settledStatus(view, diagnostics));
      if (settledFor) settledFor.current = text;
      onSettled?.(view, diagnostics);
    }
    return diagnostics;
  };
}

function settledStatus(
  view: EditorView,
  diagnostics: Diagnostic[],
): LintStatus {
  if (view.state.doc.length === 0) return "empty";
  if (diagnostics.some((d) => d.severity === "error")) return "errors";
  return diagnostics.length > 0 ? "placeholders" : "clean";
}

/** The pipeline's three roles as the toolbar's progress steps name them. */
export type StageRole = "source" | "transform" | "sink";

const STAGE_ID_ROLE: ReadonlyArray<[prefix: string, role: StageRole]> = [
  ["source", "source"],
  ["mw-", "transform"],
  ["sink-", "sink"],
];

/** The roles whose stage entries carry an error diagnostic — what keeps a
 *  toolbar progress step from reading as completed. Diagnostics outside
 *  any stage (the name, the schemas block) belong to no role. */
export function erroredStageRoles(
  state: EditorState,
  diagnostics: Diagnostic[],
): Set<StageRole> {
  const roles = new Set<StageRole>();
  for (const diagnostic of diagnostics) {
    if (diagnostic.severity !== "error") continue;
    const role = roleOfStageId(stageIdAtPosition(state, diagnostic.from));
    if (role) roles.add(role);
  }
  return roles;
}

function roleOfStageId(id: string | null): StageRole | null {
  if (id === null) return null;
  return STAGE_ID_ROLE.find(([prefix]) => id.startsWith(prefix))?.[1] ?? null;
}

/** A doc error as the editor's diagnostic. Placeholder warnings carry a
 *  mark class so the theme can drop their wavy underline — the ghost hint
 *  drawn between the quotes (emptySlotHintsView.ts) marks the slot; the
 *  gutter mark and the tooltip stay. */
export function diagnosticFor(
  state: EditorState,
  error: PipelineDocError,
): Diagnostic {
  return {
    ...findNodeRange(state, error.path),
    severity: error.severity ?? "error",
    message: error.message,
    ...(error.severity === "warning" ? { markClass: "cm-slot-warning" } : {}),
  };
}

/** CodeMirror lint source: JSON syntax errors first, then structure, then
 *  live reference checks against the engine (async, cached). */
export async function pipelineLinter(view: EditorView): Promise<Diagnostic[]> {
  if (view.state.doc.length === 0) return [];

  const syntaxErrors = jsonParseLinter()(view);
  if (syntaxErrors.length > 0) return syntaxErrors;

  const parsed = JSON.parse(view.state.doc.toString());
  const structural = validatePipelineDoc(parsed);
  const refs = collectPipelineRefs(parsed);
  const references = refErrors(refs, await fetchRefLookups(refs));

  return [...structural, ...references].map((error) =>
    diagnosticFor(view.state, error),
  );
}
